import {
  matchmakingV2MessageSchema,
  MatchmakingV2MessageType,
  type MatchmakingV2CampaignSwitchPayload,
  type MatchmakingV2ErrorPayload,
  type MatchmakingV2MatchAssignedPayload,
} from '@playcastdotio/Messaging';
import * as Sentry from '@sentry/react';
import { gameLaunchCoordinator, handleAdminRestartInstanceMessage } from '../../../coordinators/init';
import { handleCapacityConfigMessage } from './capacityConfig';

/**
 * Sink for host-bound matchmakingV2 messages. Structural subset of
 * `MatchLifecycleManager` — keeps this handler testable without the
 * coordinator graph.
 */
export type MatchmakingV2HostMessageHandler = {
  handleMatchAssigned(payload: MatchmakingV2MatchAssignedPayload): void;
  handleCampaignSwitch(payload: MatchmakingV2CampaignSwitchPayload): void;
  /** API rejection of a host-originated v2 message (e.g. flag-off). Optional so minimal test doubles stay valid. */
  handleV2Error?(payload: MatchmakingV2ErrorPayload): void;
};

const MATCHMAKING_V2_PREFIX = 'MATCHMAKING_V2_';

/**
 * Handle one raw inbound websocket message.
 *
 * matchmakingV2 messages are validated by their own union schema — they are
 * intentionally not part of `websocket.validateWebsocketMessage` yet — so the
 * realtime dispatcher offers every raw message to this handler first.
 *
 * Returns `true` when the message belonged to the matchmakingV2 namespace
 * (handled, or safely ignored: unknown/other v2 messages and invalid payloads
 * are logged and dropped). Returns `false` for non-v2 messages so the caller
 * can continue with the legacy dispatch chain.
 */
export const createMatchmakingV2Handler = (
  getManager: () => MatchmakingV2HostMessageHandler,
) => {
  return (rawMessage: unknown): boolean => {
    let raw = rawMessage;
    if (typeof raw === 'string') {
      // Fast path: don't double-parse the constant stream of non-v2 messages.
      if (!raw.includes(MATCHMAKING_V2_PREFIX)) return false;
      try {
        raw = JSON.parse(raw);
      } catch {
        return false;
      }
    }
    if (!raw || typeof raw !== 'object' || !('type' in raw)) return false;
    const type = (raw as { type: unknown }).type;
    if (typeof type !== 'string' || !type.startsWith(MATCHMAKING_V2_PREFIX)) return false;

    const parsed = matchmakingV2MessageSchema.safeParse(raw);
    if (!parsed.success) {
      Sentry.logger.error(`[MatchmakingV2-Handler] Invalid message dropped: ${type} — ${parsed.error.message}`);
      return true;
    }

    const message = parsed.data;
    Sentry.logger.info(`[MatchmakingV2-Handler] Dispatching: ${message.type}`);
    switch (message.type) {
      case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_ASSIGNED:
        getManager().handleMatchAssigned(message);
        break;
      case MatchmakingV2MessageType.MATCHMAKING_V2_CAMPAIGN_SWITCH:
        getManager().handleCampaignSwitch(message);
        break;
      case MatchmakingV2MessageType.MATCHMAKING_V2_HOST_CAPACITY_CONFIG:
        // Ops capacity push (Phase 6, G3) — applies to the host capacity
        // config store; the resulting state report confirms application.
        handleCapacityConfigMessage(message);
        break;
      case MatchmakingV2MessageType.MATCHMAKING_V2_ADMIN_RESTART_INSTANCE:
        // Admin-initiated per-instance restart (production-readiness Phase 5,
        // R5 host half). The binding is assembled in coordinators/init.ts;
        // unknown indices / no active stream are logged and ignored there.
        handleAdminRestartInstanceMessage(message);
        break;
      case MatchmakingV2MessageType.MATCHMAKING_V2_ERROR:
        Sentry.logger.error(`[MatchmakingV2-Handler] V2 ERROR from API: ${JSON.stringify(message)}`);
        getManager().handleV2Error?.(message);
        break;
      default:
        // Player-facing or host-originated v2 messages are not for the host
        // to consume — ignore safely.
        Sentry.logger.warn(`[MatchmakingV2-Handler] Ignoring v2 message not consumed by host: ${message.type}`);
    }
    return true;
  };
};

/** Production binding: routes host-bound v2 messages to the MatchLifecycleManager. */
export const handleMatchmakingV2Message = createMatchmakingV2Handler(
  () => gameLaunchCoordinator.getMatchLifecycleManager(),
);
